// Finance Books — Main shell page (Zoho Books style layout).
// Left rail navigation · BSC dark theme · section router.
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FB_NAV, BSC_ORANGE, BSC_GREEN } from './finance_books/ui';

import Dashboard from './finance_books/sections/Dashboard';
import { Customers, Vendors, Items, ChartOfAccounts } from './finance_books/sections/Masters';
import {
  Estimates, SalesOrders, Invoices, CreditNotes,
  PurchaseOrders, Bills, DebitNotes, GRNs,
} from './finance_books/sections/Documents';
import { PaymentsReceived, VendorPayments } from './finance_books/sections/Payments';
import Inventory from './finance_books/sections/Inventory';
import { FinancialReports, GSTReturns, Journals, Settings } from './finance_books/sections/Reports';
import { Projects, Budgets } from './finance_books/sections/Projects';

const SECTIONS = {
  dashboard: Dashboard,
  customers: Customers,
  vendors: Vendors,
  items: Items,
  'chart-of-accounts': ChartOfAccounts,
  estimates: Estimates,
  'sales-orders': SalesOrders,
  invoices: Invoices,
  'payments-received': PaymentsReceived,
  'credit-notes': CreditNotes,
  'purchase-orders': PurchaseOrders,
  grns: GRNs,
  bills: Bills,
  'vendor-payments': VendorPayments,
  'debit-notes': DebitNotes,
  inventory: Inventory,
  journals: Journals,
  reports: FinancialReports,
  gst: GSTReturns,
  settings: Settings,
  projects: Projects,
  budgets: Budgets,
};

export default function FinanceBooks() {
  const [section, setSection] = useState('dashboard');
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const Active = useMemo(() => SECTIONS[section] || Dashboard, [section]);

  return (
    <div className="min-h-screen flex bg-black text-white" data-testid="finance-books-shell">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 bg-neutral-950/90 flex flex-col">
        <div className="px-5 py-5 border-b border-white/10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
            data-testid="fb-back-home"
          >
            <span className="text-[18px] tracking-[0.3em] font-light text-white/90" style={{ fontFamily: "'Monoton', cursive" }}>
              <span style={{ color: BSC_ORANGE }}>F</span>
              <span>I</span>
              <span style={{ color: BSC_GREEN }}>N</span>
              <span>A</span>
              <span style={{ color: BSC_ORANGE }}>N</span>
              <span>C</span>
              <span style={{ color: BSC_GREEN }}>E</span>
            </span>
          </button>
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/35 mt-1">Books · GST · Accounting</p>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {FB_NAV.map((it, i) => {
            if (it.kind === 'heading') return (
              <div key={i} className="px-5 mt-4 mb-2 text-[9px] tracking-[0.3em] uppercase text-white/30">{it.label}</div>
            );
            const active = section === it.key;
            return (
              <button
                key={it.key}
                onClick={() => setSection(it.key)}
                className={`w-full flex items-center gap-3 px-5 py-2 text-left text-sm transition relative ${
                  active ? 'bg-white/[0.06] text-white' : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
                }`}
                data-testid={`fb-nav-${it.key}`}
              >
                {active && <span className="absolute left-0 top-0 bottom-0 w-[2px]" style={{ backgroundColor: BSC_ORANGE }} />}
                <span className="text-white/40 text-xs">{it.icon || '·'}</span>
                <span>{it.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-4 text-xs">
          <div className="text-white/70 truncate">{user?.name}</div>
          <div className="text-white/35 truncate">{user?.email}</div>
          <button onClick={async () => { await logout(); navigate('/'); }} className="mt-2 text-white/40 hover:text-white text-[11px]" data-testid="fb-logout">
            Log out →
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-[1500px] mx-auto px-8 py-8">
          <Active onNav={setSection} />
        </div>
      </main>
    </div>
  );
}
